import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, BookOpen, CheckCircle } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';
import { markFormLoaded, isSubmitTooFast, checkRateLimit, sanitizeText, validateFormField } from '../utils/formValidation';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  useEffect(() => { markFormLoaded('forgot'); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitTooFast('forgot')) return;
    if (!checkRateLimit('forgot', 5000)) { setError('Please wait a moment.'); return; }
    const emailCheck = validateFormField(email, 'email');
    if (!emailCheck.valid) { setError(emailCheck.error); return; }
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/forgot-password', { email: sanitizeText(email) });
      setSent(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <>
        <SeoHead title="Check Your Email — AI Ghostwriter Pro" noindex={true} />
        <section className="min-h-screen flex items-center justify-center px-4 pt-20">
          <div className="w-full max-w-md text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h1 className="font-serif text-2xl tracking-tight mb-2">Check Your Email</h1>
            <p className="text-sm text-muted-foreground mb-6">If an account exists for <strong className="text-foreground">{email}</strong>, we've sent a password reset link. It expires in 1 hour.</p>
            <Link to="/login" className="text-primary hover:underline text-sm" data-testid="forgot-to-login">Back to Login</Link>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SeoHead title="Forgot Password — AI Ghostwriter Pro" noindex={true} />
      <section className="min-h-screen flex items-center justify-center px-4 pt-20">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <BookOpen className="w-6 h-6 text-primary" strokeWidth={1.5} />
              <span className="font-serif text-xl text-foreground">AI Ghostwriter <span className="text-primary">Pro</span></span>
            </Link>
            <h1 className="font-serif text-2xl sm:text-3xl tracking-tight mb-2">Forgot Password?</h1>
            <p className="text-sm text-muted-foreground">Enter your email and we'll send you a reset link</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-form">
            {error && <p className="text-sm text-red-400 text-center" data-testid="forgot-error">{error}</p>}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                placeholder="Your email address" data-testid="forgot-email" maxLength={254}
                className="w-full pl-10 pr-4 h-12 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-1 focus:ring-primary/50 outline-none" />
            </div>
            <button type="submit" disabled={loading} data-testid="forgot-submit"
              className="w-full rounded-sm py-3 bg-primary text-primary-foreground font-semibold tracking-wide transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:bg-white hover:text-black">
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>

          <p className="text-center mt-6 text-sm text-muted-foreground">
            Remember your password? <Link to="/login" className="text-primary hover:underline" data-testid="forgot-to-login-link">Sign in</Link>
          </p>
        </div>
      </section>
    </>
  );
}
